import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ordersAPI, exportsAPI } from '../services/api';
import { orderCustomerListLabel } from '../utils/orderCustomer';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './Users.css';

const initialPerPage = () => {
  const stored = parseInt(localStorage.getItem('per_page') || '', 10);
  return [10, 15, 25, 50, 100].includes(stored) ? stored : 15;
};

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: initialPerPage(),
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const prevDebouncedSearch = useRef('');

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (statusFilter) params.status = statusFilter;
      if (paymentFilter) params.payment_status = paymentFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await ordersAPI.getAll(params);
      setOrders(response.data.data || []);
      const pg = response.data.pagination;
      if (pg) {
        setPagination((prev) => {
          const nextPage = pg.page ?? prev.page;
          const nextPerPage = pg.per_page ?? prev.per_page;
          const nextTotal = pg.total ?? 0;
          if (
            prev.page === nextPage &&
            prev.per_page === nextPerPage &&
            prev.total === nextTotal
          ) {
            return prev;
          }
          return { ...prev, page: nextPage, per_page: nextPerPage, total: nextTotal };
        });
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.per_page,
    statusFilter,
    paymentFilter,
    debouncedSearch,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  const handleViewDetails = (id) => {
    navigate(`/orders/${id}`);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setPaymentFilter('');
    setSearchInput('');
    setDebouncedSearch('');
    prevDebouncedSearch.current = '';
    setDateFrom('');
    setDateTo('');
    setPagination((p) => ({ ...p, page: 1 }));
  };

  if (loading && orders.length === 0) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="users-container">
      <div className="search-filter-bar">
        <div className="filter-field">
          <label htmlFor="orders-status">Order status</label>
          <select
            id="orders-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="reserved">Reserved (deposit)</option>
            <option value="shipped">Shipped</option>
            <option value="out_for_delivery">Out for delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="orders-payment">Payment</label>
          <select
            id="orders-payment"
            value={paymentFilter}
            onChange={(e) => {
              setPaymentFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="deposit_paid">Deposit paid</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        <div className="filter-field filter-field--grow">
          <label htmlFor="orders-search">Order # or buyer</label>
          <input
            id="orders-search"
            type="search"
            placeholder="Search by order number, name, or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="filter-field">
          <label htmlFor="orders-from">From date</label>
          <input
            id="orders-from"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>

        <div className="filter-field">
          <label htmlFor="orders-to">To date</label>
          <input
            id="orders-to"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>

        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
          Clear filters
        </button>
        <ExportCsvButton
          exportRequest={exportsAPI.orders}
          filters={{
            status: statusFilter || undefined,
            payment_status: paymentFilter || undefined,
            search: debouncedSearch || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          }}
          filename="orders.csv"
        />
      </div>

      {loading && orders.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Buyer / customer</th>
              <th>Total</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="table-empty">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id} className={`table-row-status table-row-status-${order.status || 'default'}`}>
                  <td>{order.order_number}</td>
                  <td>{orderCustomerListLabel(order)}</td>
                  <td>
                    {order.currency || 'AED'} {order.total_price}
                  </td>
                  <td>{order.status}</td>
                  <td>{order.payment_status}</td>
                  <td className="actions">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(order.id);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total > pagination.per_page && (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
          onPerPageChange={(per_page) => setPagination((p) => ({ ...p, per_page, page: 1 }))}
        />
      )}
    </div>
  );
};

export default Orders;
